import React from 'react';
import classNames from 'classnames';
import { FaChevronRight } from 'react-icons/fa6';

interface BlockLayoutProps {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  actionLink?: string;
  openInNewTab?: boolean;
  headerClassName?: string;
  showChevron?: boolean;
  children: React.ReactNode;
  variant?: 'default' | 'clean' | 'dark';
  icon?: React.ReactNode;
  fillHeight?: boolean;
}

export const BlockLayout = ({
  title,
  subtitle,
  actionLabel,
  actionLink,
  openInNewTab = true,
  headerClassName,
  showChevron = false,
  children,
  variant = 'default',
  icon,
  fillHeight,
}: BlockLayoutProps) => {
  const isClean = variant === 'clean';
  const isDark = variant === 'dark';

  return (
    <div
      className={classNames('rounded-lg overflow-hidden', {
        'border border-[#CEDDF2]': !isClean && !isDark,
        'border-none bg-transparent': isClean,
        'border border-[#2d4a6f] bg-[#1a2942]': isDark,
        'h-full flex flex-col': fillHeight,
      })}
    >
      <div
        className={classNames(
          'flex flex-row justify-between items-center flex-wrap',
          {
            'py-2.5 px-2.5 bg-[#F2F8FF]': !isClean && !isDark,
            'bg-transparent': isClean,
            'py-2.5 px-2.5 bg-transparent': isDark,
          },
          headerClassName,
        )}
      >
        <div className="flex flex-col">
          <h2
            className={classNames(
              'font-bold text-base leading-6 align-middle whitespace-nowrap pr-2 flex flex-row items-center gap-2',
              {
                'text-white': isDark,
              },
            )}
          >
            {icon && <span className="mr-2 flex items-center">{icon}</span>}
            {title}
          </h2>
          {subtitle && (
            <span
              className={classNames('text-xs font-normal normal-case leading-[1.4]', {
                'text-gray-500': !isDark,
                'text-gray-400': isDark,
              })}
            >
              {subtitle}
            </span>
          )}
        </div>
        {actionLink && (
          <a
            className={classNames(
              'flex flex-row justify-end items-center gap-1 cursor-pointer font-bold text-base capitalize text-[var(--color-palette-blue500)]',
              {
                'hover:text-blue-400': isDark,
              },
            )}
            href={actionLink}
            aria-label={`${actionLabel} ${title}`}
            {...(openInNewTab && { target: '_blank' })}
          >
            {actionLabel} {showChevron && <FaChevronRight size={12} />}
          </a>
        )}
      </div>
      <div
        className={classNames({
          'bg-[#F2F8FF] px-2.5 pb-2.5': !isClean && !isDark,
          'bg-transparent': isClean,
          'bg-transparent px-2.5 pb-2.5': isDark,
          'flex-1 flex flex-col': fillHeight,
        })}
      >
        {children}
      </div>
    </div>
  );
};
